import { adaloBackendAxios } from 'utils/io/http/axios'
import { SHEETBRIDGE_ID_COLUMN } from 'config'

export const buildAuth = sheet =>
  sheet.authHeaderEncrypted
    ? [
        {
          type: 'header',
          name: sheet.authHeaderName || 'Authorization',
          value: sheet.authHeaderEncrypted,
          encrypted: true,
        },
      ]
    : []

const introspectFields = async (baseURL, sheet) => {
  const { getFields } = await import('utils/external-collections')

  const auth = buildAuth(sheet)
  const QS = await import('qs')
  const query = QS.default.stringify({ url: baseURL, auth, method: 'get' })

  const { data: testResponse } = await adaloBackendAxios.get(
    `/test-api-endpoint?${query}`
  )

  if (!testResponse.success || !testResponse.response?.data) {
    return null
  }

  const results = Array.isArray(testResponse.response.data)
    ? testResponse.response.data
    : [testResponse.response.data]

  if (results.length === 0) return null

  const [fields, orderedFields] = getFields(results)

  // Force sheetbridge_id as primary when available
  if (SHEETBRIDGE_ID_COLUMN && SHEETBRIDGE_ID_COLUMN in fields) {
    Object.values(fields).forEach(f => {
      f.isPrimaryField = false
    })
    fields[SHEETBRIDGE_ID_COLUMN].isPrimaryField = true

    return {
      fields,
      orderedFields: [
        SHEETBRIDGE_ID_COLUMN,
        ...orderedFields.filter(f => f !== SHEETBRIDGE_ID_COLUMN),
      ],
    }
  }

  return { fields, orderedFields }
}

export const getFallbackFields = () => ({
  fields: { id: { name: 'id', type: 'text', isPrimaryField: true } },
  orderedFields: ['id'],
})

export const getPrimaryKey = fields => {
  if (SHEETBRIDGE_ID_COLUMN && SHEETBRIDGE_ID_COLUMN in fields) {
    return SHEETBRIDGE_ID_COLUMN
  }

  const primary = Object.entries(fields).find(([, f]) => f.isPrimaryField)

  return primary ? primary[0] : Object.keys(fields)[0]
}

export const buildEndpoints = (baseURL, appendURLBinding) => ({
  list: { enabled: true, method: 'get', url: baseURL },
  detail: {
    enabled: true,
    method: 'get',
    url: appendURLBinding(baseURL, '/{{id}}'),
  },
  create: { enabled: true, method: 'post', url: baseURL },
  update: {
    enabled: true,
    method: 'put',
    url: appendURLBinding(baseURL, '/{{id}}'),
  },
  delete: {
    enabled: true,
    method: 'delete',
    url: appendURLBinding(baseURL, '/{{id}}'),
  },
})

export const parseSheetKey = sheetKey => {
  const lastDashIndex = sheetKey.lastIndexOf('-')

  return {
    bridgeId: sheetKey.substring(0, lastDashIndex),
    sheetId: sheetKey.substring(lastDashIndex + 1),
  }
}

export const findSheet = (bridges, bridgeId, sheetId) => {
  const bridge = bridges.find(b => String(b.id) === String(bridgeId))
  if (!bridge) return null

  const sheet = (bridge.sheets || []).find(
    s => String(s.id) === String(sheetId)
  )

  return sheet || null
}

export const createCollections = async ({
  selectedSheetKeys,
  bridges,
  dispatch,
}) => {
  const { getTableId } = await import('@adalo/utils')
  const { stripTrailingSlash, appendURLBinding } = await import(
    'utils/bindings'
  )

  for (const sheetKey of selectedSheetKeys) {
    const { bridgeId, sheetId } = parseSheetKey(sheetKey)
    const sheet = findSheet(bridges, bridgeId, sheetId)
    if (!sheet) continue

    const baseURL = stripTrailingSlash(sheet.apiUrl)

    let fieldData
    try {
      fieldData = await introspectFields(baseURL, sheet)
    } catch (error) {
      console.warn('Failed to introspect API, using minimal fields:', error)
    }

    const { fields, orderedFields } = fieldData || getFallbackFields()
    const primaryKeyField = getPrimaryKey(fields)

    const tableData = {
      id: getTableId(),
      name: sheet.name,
      type: 'api',
      tableType: 'api',
      tableSource: 'sheetbridge',
      baseURL,
      endpoints: buildEndpoints(baseURL, appendURLBinding),
      fields,
      orderedFields,
      auth: buildAuth(sheet),
      ...(primaryKeyField ? { primaryKeyField, primaryKeyType: 'id' } : {}),
    }

    dispatch(tableData)
  }
}
